import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Trophy, Zap, Star, Heart, Home, RotateCcw, Share2, X, Medal, User } from 'lucide-react';
import { cn } from '../lib/utils';
import CurrencyIcon from './CurrencyIcon';
import { db } from '../firebase';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { format } from 'date-fns';
import { audioEngine } from '../utils/audioEngine';
import LevelMedal from './LevelMedal';
import { Prism } from '../utils/prismSystem';
import Prism3D from './Prism3D';
import { useTranslation } from 'react-i18next';

interface GameOverProps {
  score: number;
  level: number;
  expGained: number;
  rank?: number;
  isMonthlyBest?: boolean;
  isAllTimeBest?: boolean;
  onRestart: () => void;
  onHome: () => void;
  mode: 'pemdas' | 'shellcrush' | 'starburst' | 'goldrush';
  prismBuffs: any;
  saveStatus: 'idle' | 'saving' | 'success' | 'error';
  gemsGained?: number;
  coinsGained?: number;
  isPractice?: boolean;
  onClaimPractice?: () => void;
  starTickets?: number;
  equippedPrism?: Prism | null;
}

export default function GameOver({ 
  score, 
  level, 
  expGained, 
  rank,
  isMonthlyBest,
  isAllTimeBest,
  onRestart, 
  onHome,
  mode,
  prismBuffs,
  saveStatus,
  gemsGained = 0,
  coinsGained = 0,
  isPractice = false,
  onClaimPractice,
  starTickets = 0,
  equippedPrism = null
}: GameOverProps) {
  const { t } = useTranslation();
  const [showRankings, setShowRankings] = useState(false);
  const [rankings, setRankings] = useState<any[]>([]);
  const [loadingRankings, setLoadingRankings] = useState(false);

  const fetchRankings = async () => {
    setLoadingRankings(true);
    try {
      const monthLabel = format(new Date(), 'yyyy-MM');
      const q = query(
        collection(db, 'scores'),
        where('mode', '==', mode),
        where('monthLabel', '==', monthLabel),
        orderBy('score', 'desc'),
        limit(50)
      );
      const snap = await getDocs(q);
      const allData = snap.docs.map(d => d.data());
      
      const unique: Record<string, any> = {};
      allData.forEach((d: any) => {
        if (!unique[d.uid] || d.score > unique[d.uid].score) {
          unique[d.uid] = d;
        }
      });
      
      setRankings(Object.values(unique).sort((a, b) => b.score - a.score).slice(0, 10));
    } catch (err) {
      console.error("Error fetching rankings preview:", err);
    } finally {
      setLoadingRankings(false);
    }
  };

  useEffect(() => {
    if (showRankings) {
      fetchRankings();
    }
  }, [showRankings]);

  useEffect(() => {
    audioEngine.playGameOver();
    
    if (!isPractice && (isAllTimeBest || isMonthlyBest)) {
      const t = setTimeout(() => audioEngine.playHighScore(), 600);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <div className="flex flex-col h-full night-sky-bg text-white p-6 items-center justify-center relative overflow-hidden">
      {/* Premium Ambient Light Glows */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/3 w-60 h-60 rounded-full bg-purple-500/5 blur-3xl pointer-events-none" />
      
      {/* Background Animated Floating Particles */}
      <div className="absolute inset-0 pointer-events-none opacity-30">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1.5 h-1.5 bg-cyan-400 rounded-full"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -40, 0],
              opacity: [0.2, 0.8, 0.2],
              scale: [0.8, 1.2, 0.8]
            }}
            transition={{
              duration: 4 + Math.random() * 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 5
            }}
          />
        ))}
      </div>

      <AnimatePresence>
        {!isPractice && (isMonthlyBest || isAllTimeBest) && (
          <motion.div
            initial={{ scale: 0, rotate: -15, y: -50 }}
            animate={{ scale: 1, rotate: 0, y: 0 }}
            exit={{ scale: 0, opacity: 0 }}
            className={cn(
              "absolute top-12 z-[100] font-num text-xl px-8 py-3 rounded-[32px] shadow-2xl text-center border border-white/20 select-none",
              isAllTimeBest ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white scale-110" : "bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-950 font-black"
            )}
          >
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className="flex items-center gap-2"
            >
              {isAllTimeBest ? t('gameOver.new_world_record') : t('gameOver.new_monthly_best')}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 35 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        className="w-full max-w-sm glass-panel p-7 relative overflow-hidden z-10 rounded-[40px] border border-white/10 shadow-2xl"
      >
        <div className="text-center relative z-10">
          
          <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.25em] mb-1">{t('gameOver.score_label')}</h2>
          
          {/* Score display */}
          <div className="mb-6">
            <h1 className={cn(
              "text-7xl font-num font-black tracking-tight drop-shadow-md select-none bg-clip-text text-transparent",
              (isAllTimeBest || isMonthlyBest) 
                ? "bg-gradient-to-b from-yellow-300 via-amber-400 to-orange-500" 
                : "bg-gradient-to-b from-white to-slate-300"
            )}>
              {score}
            </h1>
            {isPractice && (
              <span className="inline-block mt-1 bg-white/10 backdrop-blur-md text-cyan-300 text-[8px] font-black px-2 py-0.5 rounded-full border border-cyan-500/20 uppercase tracking-widest">
                {t('gameOver.practice_mode')}
              </span>
            )}
            {prismBuffs?.scoreMultiplier > 0 && (
              <div className="inline-block mt-2 px-3 py-1 bg-rose-500/10 border border-rose-500/20 text-rose-400 text-[10px] font-black uppercase rounded-full tracking-wider">
                {t('gameOver.prism_boost', { value: Math.round(prismBuffs.scoreMultiplier * 100) })}
              </div>
            )}
          </div>



          {/* Rewards overview */}
          {!isPractice && (
            <div className="bg-white/5 border border-white/10 p-4 rounded-3xl mb-6 text-left">
              <h3 className="text-[8px] font-black text-slate-400 uppercase tracking-[0.25em] mb-4 text-center">{t('gameOver.earned_this_run')}</h3>
              <div className="flex items-center justify-around">
                <div className="flex flex-col items-center gap-1.5">
                  <CurrencyIcon type="coin" size="md" />
                  <span className="font-num text-base font-black text-yellow-400">+{coinsGained}</span>
                </div>

                {gemsGained > 0 && (
                  <>
                    <div className="w-px h-8 bg-white/10" />
                    <div className="flex flex-col items-center gap-1.5">
                      <CurrencyIcon type="gem" size="md" />
                      <span className="font-num text-base font-black text-purple-400">+{gemsGained}</span>
                    </div>
                  </>
                )}

                <div className="w-px h-8 bg-white/10" />
                <div className="flex flex-col items-center gap-1.5">
                  <CurrencyIcon type="exp" size="md" />
                  <span className="font-num text-base font-black text-cyan-400">+{expGained}</span>
                </div>
              </div>
            </div>
          )}

          {/* Equipped Core Overview */}
          {equippedPrism && mode !== 'starburst' && (
            <div className="bg-white/5 border border-white/10 p-3.5 rounded-[24px] mb-5 text-left flex items-center gap-4 relative overflow-hidden">
              <div 
                className="absolute inset-0 opacity-[0.03] pointer-events-none" 
                style={{ 
                  backgroundColor: equippedPrism.color === 'yellow' ? '#fbbf24' :
                                   equippedPrism.color === 'green' ? '#10b981' :
                                   equippedPrism.color === 'blue' ? '#38bdf8' :
                                   equippedPrism.color === 'violet' ? '#a855f7' : '#f43f5e'
                }} 
              />
              
              <div 
                className="w-14 h-14 bg-black/30 rounded-2xl flex items-center justify-center shrink-0 border relative"
                style={{
                  borderColor: equippedPrism.stars === 3 ? '#fbbf24' : 
                               equippedPrism.color === 'yellow' ? '#fbbf24' :
                               equippedPrism.color === 'green' ? '#10b981' :
                               equippedPrism.color === 'blue' ? '#38bdf8' :
                               equippedPrism.color === 'violet' ? '#a855f7' : '#f43f5e'
                }}
              >
                <div className="scale-75 w-12 h-12 flex items-center justify-center">
                  <Prism3D 
                    color={equippedPrism.color} 
                    stars={equippedPrism.stars} 
                    isCorrupted={!!equippedPrism.blackStats} 
                    potentials={[equippedPrism.slot1, equippedPrism.slot2, equippedPrism.slot3].filter(Boolean) as any}
                    isStatic={true} 
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-[10px] font-black uppercase text-slate-200">
                    {t(`prism.cores.${equippedPrism.color}.name`)}
                  </span>
                  <div className="flex gap-0.5 shrink-0">
                    {Array.from({ length: equippedPrism.stars }).map((_, i) => (
                      <Star key={i} className="w-2 h-2 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                </div>
                <div className="text-[8px] font-black uppercase text-slate-400 tracking-wider">
                  {t('prism.active_buff')}: {t(`prism.cores.${equippedPrism.color}.buff`)}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            {isPractice && mode === 'starburst' && (
              <button
                onClick={onClaimPractice}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-num text-lg py-4 flex flex-col items-center justify-center gap-0.5 toy-3d-button border-none transition-all shadow-lg hover:brightness-110 active:scale-95"
              >
                <div className="flex items-center gap-2 font-black">
                  <Star className="w-5 h-5 fill-white" />
                  {t('gameOver.claim_with_ticket')}
                </div>
                <span className="text-[8px] font-black uppercase tracking-widest opacity-80">
                  {t('gameOver.tickets_available', { count: starTickets })}
                </span>
              </button>
            )}

            {/* Retry Button */}
            <button
               onClick={() => {
                 onRestart();
               }}
              className="w-full bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-950 font-num text-2xl py-4 flex items-center justify-center gap-2 toy-3d-button border-none font-black active:scale-95 transition-all"
            >
              <RotateCcw className="w-6 h-6 stroke-[3px]" />
              {t('gameOver.retry')}
            </button>
            
            <div className="flex items-center justify-center gap-1 -mt-1 mb-2 select-none">
              <Heart className={cn("w-3 h-3", isPractice ? "text-slate-500 fill-slate-500" : "text-rose-500 fill-rose-500")} />
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                {isPractice ? t('gameOver.free_practice') : t('gameOver.heart_cost')}
              </span>
            </div>

            {/* Home Button */}
            <button
              onClick={onHome}
              className="w-full bg-white/10 hover:bg-white/20 border border-white/15 text-white font-black py-3.5 flex items-center justify-center gap-2 toy-3d-button border-none transition-all"
            >
              <Home className="w-4 h-4 text-yellow-400" />
              {t('gameOver.home')}
            </button>
          </div>
        </div>
      </motion.div>

      {/* Rankings Preview Modal */}
      <AnimatePresence>
        {showRankings && (
          <div className="absolute inset-0 z-[200] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-950/70 backdrop-blur-md"
              onClick={() => setShowRankings(false)}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="bg-slate-900 border border-white/10 w-full max-w-sm p-6 relative z-10 rounded-[40px] shadow-2xl flex flex-col max-h-[80vh] text-white"
            >
              <button 
                onClick={() => setShowRankings(false)}
                className="absolute top-5 right-5 p-2 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <h3 className="text-xl font-num font-black mb-4 tracking-tight uppercase bg-gradient-to-r from-yellow-300 to-amber-500 bg-clip-text text-transparent">{t('gameOver.rankings.title')}</h3>

              <div className="flex-1 overflow-y-auto space-y-2.5 scrollbar-none pr-1">
                {loadingRankings ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mb-4" />
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('gameOver.rankings.loading')}</span>
                  </div>
                ) : (
                  rankings.map((entry, i) => (
                    <div 
                      key={i}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-2xl border",
                        i === 0 ? "bg-yellow-400/10 border-yellow-400/30" : "bg-white/5 border-white/5"
                      )}
                    >
                      <div className="w-6 flex justify-center shrink-0">
                        {i === 0 ? <Medal className="w-5 h-5 text-yellow-400" /> :
                         i === 1 ? <Medal className="w-5 h-5 text-slate-300" /> :
                         i === 2 ? <Medal className="w-5 h-5 text-amber-600" /> :
                         <span className="text-slate-500 font-num font-black text-sm">{i + 1}</span>}
                      </div>
                      {entry.level ? (
                        <LevelMedal level={entry.level} size="sm" className="shrink-0" />
                      ) : (
                        <img 
                          src={entry.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${entry.uid}`} 
                          className="w-9 h-9 rounded-xl border border-white/10 shadow-sm bg-white" 
                          alt="Avatar" 
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-black text-[10px] truncate uppercase tracking-tight text-white">
                          {entry.displayName || t('gameOver.rankings.anonymous')}
                        </div>
                      </div>
                      <div className="text-base font-num font-black text-cyan-400 tabular-nums">
                        {entry.score}
                      </div>
                    </div>
                  ))
                )}
                {!loadingRankings && rankings.length === 0 && (
                  <div className="text-center py-12">
                     <Trophy className="w-12 h-12 text-slate-700 mx-auto mb-2" />
                     <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest animate-pulse">{t('gameOver.rankings.no_rankings')}</p>
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowRankings(false)}
                className="w-full mt-5 bg-white text-slate-900 font-black py-3.5 rounded-2xl toy-3d-button border-none hover:bg-slate-100 active:scale-95"
              >
                {t('gameOver.rankings.got_it')}
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
